import { useCallback, useEffect, useState } from 'react';

import type { CursorData } from '@aline/common';
import {
  AlineTransforms,
  CollaborationEditor,
  CursorEditor,
  Queries,
  SelEventSource,
} from '@aline/common';
import type { HocuspocusProvider } from '@hocuspocus/provider';
import { Editor } from 'slate';
import { useEditorTabStateActionsForTabId } from 'src/editor/editor-tab-state-registry';
import { useCollabDocument } from 'src/editor/helpers/useCollabDocument';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserQuery } from 'src/gql';
import { getDecodedHash } from 'src/helpers';
import { useGetCurrentDoc, usePrevious } from 'src/hooks';
import type { AlineClientEditor } from 'src/layout-engine';
import { useEditor, useSelection } from 'src/layout-engine';
import { useCurrentTabIdOrThrow } from 'src/layouts/tabs/tab-active-context';
import type * as Y from 'yjs';

import type { DocumentWithLatestVersion } from '..';

export function useLoadCollaborativeDocAndFonts(): {
  connected: boolean;
  connectProvider: () => void;
  destroyProvider: () => void;
  editor: AlineClientEditor;
  editorLoading: boolean;
  provider: HocuspocusProvider;
} {
  const tabId = useCurrentTabIdOrThrow();
  const { document } = useGetCurrentDoc();
  const { connected, destroyProvider, editor, provider, sharedRoot } = useCollabDocument();
  const {
    viewer: { setViewerPageWidthPx },
  } = useEditorTabStateActionsForTabId(tabId);
  const [loadedSharedRoot, setLoadedSharedRoot] = useState<Y.XmlText | null>(null);

  const connectProvider = useCallback(() => {
    const connectEditor = () => {
      // If the sharedRoot has content, it's loaded from remote => update the loading state and unregister our checkLoaded observer.
      if (sharedRoot.toDelta().length) {
        Promise.resolve().then(() => {
          /**
           * Connect sets the editor children to the remote (sharedRoot), force-normalizes the entire Slate doc, and calls onChange internally.
           * See: https://github.com/BitPhinix/slate-yjs/blob/main/packages/core/src/plugins/withYjs.ts#L204-L218
           */
          CollaborationEditor.connect(editor);

          // Must be *after* CollaborationEditor.connect so editor.children is set correctly.
          const [editorDocument, editorDocumentPath] = Queries.getDocument(editor);
          const documentStart = Editor.start(editor, editorDocumentPath);
          const { width: widthPx } = Queries.activePageDimensions(editorDocument, 'px');

          setViewerPageWidthPx(widthPx);

          // Select a bookmark if linked to in the URL fragment, otherwise select the start of the document.
          const hash = getDecodedHash();
          const selectedBookmark = AlineTransforms.selectBookmark(editor, hash);

          if (!selectedBookmark) {
            AlineTransforms.setSelection(editor, {
              eventSource: SelEventSource.PROGRAMMATIC,
              props: { affinity: 'forward', anchor: documentStart, focus: documentStart },
            });
          }

          ClientTransforms.focus(editor);
          setLoadedSharedRoot(sharedRoot);
          sharedRoot.unobserve(connectEditor);
        });
      }
    };

    sharedRoot.observe(connectEditor);
  }, [editor, setViewerPageWidthPx, sharedRoot]);

  /**
   * Destroy the old provider and reconnect if the provider changes. This can happen when navigating
   * directly from one document to another within the same tab, such as when switching between
   * documents in a workflow.
   */
  const previousProvider = usePrevious(provider);
  if (provider !== previousProvider) {
    destroyProvider(previousProvider);
    connectProvider();
  }

  return {
    connectProvider,
    connected,
    destroyProvider,
    editor,
    editorLoading: getIsEditorLoading(document, { loadedSharedRoot, sharedRoot }),
    provider,
  };
}

function getIsEditorLoading(
  document: DocumentWithLatestVersion | null,
  options: { loadedSharedRoot: Y.XmlText | null; sharedRoot: Y.XmlText }
): boolean {
  const { loadedSharedRoot, sharedRoot } = options;

  // Document still being fetched from the server...
  if (!document) return true;

  switch (document.latestVersion.__typename) {
    case 'DocumentVersion':
      return loadedSharedRoot !== sharedRoot;
    default:
      return false;
  }
}

export function useSyncCursorData() {
  const editor = useEditor();
  const selection = useSelection();
  const { currentUser } = useCurrentUserQuery();

  useEffect(() => {
    if (CursorEditor.isCursorEditor(editor)) {
      CursorEditor.sendCursorData(editor, {
        affinity: selection?.affinity ?? 'forward',
        avatarColor: currentUser?.avatarColor,
        displayName: currentUser?.displayName,
        gridSelectionAnchorColumn: selection?.gridSelectionAnchorColumn ?? null,
        gridSelectionFocusColumn: selection?.gridSelectionFocusColumn ?? null,
        id: currentUser?.id,
      } satisfies CursorData);
    }
  }, [
    currentUser,
    editor,
    selection?.affinity,
    selection?.gridSelectionAnchorColumn,
    selection?.gridSelectionFocusColumn,
  ]);
}
